import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, EyeOff, Loader2, RefreshCw, Sparkles, X, LocateFixed, CornerDownRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { DateTime } from '@/components/ui/DateTime'
import { DotSphereSpinner } from '@/components/ui/DotSphereSpinner'
import { handleApiError } from '@/lib/api/axios'
import { useCan } from '@/hooks/useCan'
import {
    LEVELS,
    SEVERITIES,
    httpStatusOf,
    type ApplyRequest,
    type ApplyResult,
    type Candidate,
    type Findings,
    type LevelKey,
    type LinkRef,
    type ObjectRef,
    type Review,
    type Rule,
    type Severity,
    type Suggestion,
} from '../api'
import { useHierarchyAccess } from '../access'
import {
    REVIEW_POLL_MS,
    useApplyAdvice,
    useDecide,
    useExclude,
    useHierarchyExclusions,
    useHierarchyFindings,
    useHierarchyRules,
    useLatestReview,
    useRemoveExclusion,
    useSetRule,
    useStartReview,
} from '../hooks'
import { orderFindings, ruleLabel } from '../rules'
import { OBJECT_ID_RE, suggestedObjectId } from '../newObject'
import { SEVERITY_COLOUR } from '../badgeStore'

/**
 * The Hierarchy Advisor's panel: what looks missing or misconfigured beneath one root.
 *
 * Decision support, never a verdict. Four levels in Andrew's order of importance (29 Sep 2026):
 * missing groups, missing objects (the advisor agent's suggestions), misconfigured objects and
 * groups, data gaps. A rule that finds forty objects is ONE line with its count, opened on demand,
 * so the panel reads as a short list of problems rather than a wall of objects.
 *
 * No rule is forced on a customer: `hierarchy.configure` holders turn rules off, change their
 * severity and exclude an object from a rule with a reason - and the panel always says how much
 * those choices hide, so a clean panel is an honest one.
 *
 * Accepting a suggestion does the work (1 Oct 2026): the object the advisor misses may already be
 * in the estate, so each suggestion offers the existing objects it could be first, and creating a
 * new one second - either way the link is made in the same step, for `objects.update` holders
 * (`objects.create` to create). Without it a person can still record that they accept.
 *
 * Rendered in two places: the planning canvas's slide-over (where clicking an object centres it)
 * and the object page's Review tab (where an object is a link to its own page).
 */
export interface HierarchyAdvisorPanelProps {
    root: string
    scenario: string | number
    /** On the canvas: select and centre the node. Answers false when the object is not drawn. */
    onSelectObject?: (objectId: string) => boolean | void
    /**
     * On the canvas: zoom in on an object and centre it, as the toolbar's search does. False when it
     * is not drawn. A suggestion card offers "Show" only where this is given.
     */
    onShowObject?: (objectId: string) => boolean | void
    /** On the canvas: a suggestion was applied - draw the object and its link. */
    onApplied?: (result: ApplyResult) => void
    /** Tests only: the contract's 3 s is the default. */
    pollIntervalMs?: number
}

type View = 'findings' | 'rules' | 'exclusions'

/**
 * The advisor's run for this root and scenario. Held by the panel, not by the findings view, so
 * moving between findings, rules and exclusions never loses it: the spinner stops when the review
 * is done or has failed, and at nothing else.
 */
interface AdvisorRun {
    /** The newest review; undefined until it has been read, null when there has never been one. */
    review: Review | null | undefined
    running: boolean
    ask: () => void
    error: unknown
}

export function HierarchyAdvisorPanel({ root, scenario, onSelectObject, onShowObject, onApplied, pollIntervalMs }: HierarchyAdvisorPanelProps) {
    const { t } = useTranslation()
    const { canReview, canConfigure } = useHierarchyAccess()
    const [view, setView] = useState<View>('findings')
    const latest = useLatestReview(root, scenario, { enabled: canReview, pollMs: pollIntervalMs ?? REVIEW_POLL_MS })
    const start = useStartReview()

    if (!canReview) return null

    const advisor: AdvisorRun = {
        review: latest.data,
        running: start.isPending || latest.data?.status === 'running',
        ask: () => start.mutate({ root, scenario }),
        error: start.isError ? start.error : null,
    }

    const views: { key: View; label: string }[] = [
        { key: 'findings', label: t('hierarchyAdvisor.views.findings') },
        ...(canConfigure ? [{ key: 'rules' as View, label: t('hierarchyAdvisor.views.rules') }] : []),
        { key: 'exclusions', label: t('hierarchyAdvisor.views.exclusions') },
    ]

    return (
        <div className="flex flex-col gap-3" data-testid="hierarchy-advisor-panel">
            <div className="flex items-center gap-2">
                <div className="flex flex-1 gap-1 rounded-md bg-muted p-0.5" role="group" aria-label={t('hierarchyAdvisor.views.label')}>
                    {views.map((v) => (
                        <button
                            key={v.key}
                            type="button"
                            aria-pressed={view === v.key}
                            onClick={() => setView(v.key)}
                            className={`flex-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
                                view === v.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
                {/* Whichever view is showing: the review runs on the server, not in the findings view. */}
                {advisor.running && (
                    <DotSphereSpinner size={36} label={t('hierarchyAdvisor.advisor.reviewingLabel')} className="shrink-0" />
                )}
            </div>
            {view === 'findings' && (
                <FindingsView
                    root={root}
                    scenario={scenario}
                    canConfigure={canConfigure}
                    onSelectObject={onSelectObject}
                    onShowObject={onShowObject}
                    onApplied={onApplied}
                    advisor={advisor}
                    onShowView={setView}
                />
            )}
            {view === 'rules' && canConfigure && <RulesView />}
            {view === 'exclusions' && <ExclusionsView root={root} canConfigure={canConfigure} onSelectObject={onSelectObject} />}
        </div>
    )
}

// ── Findings ─────────────────────────────────────────────────────────────────────────────────

function FindingsView({
    root,
    scenario,
    canConfigure,
    onSelectObject,
    onShowObject,
    onApplied,
    advisor,
    onShowView,
}: {
    root: string
    scenario: string | number
    canConfigure: boolean
    onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject']
    onShowObject?: HierarchyAdvisorPanelProps['onShowObject']
    onApplied?: HierarchyAdvisorPanelProps['onApplied']
    advisor: AdvisorRun
    onShowView: (v: View) => void
}) {
    const { t } = useTranslation()
    const findings = useHierarchyFindings(root, scenario)
    // The rules carry each one's severity override, which "Turn off" must keep.
    const rules = useHierarchyRules(canConfigure)
    const setRule = useSetRule()

    if (findings.isLoading) {
        return (
            <div className="flex items-center gap-2 py-6 text-xs text-muted-foreground" role="status">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('hierarchyAdvisor.loading')}
            </div>
        )
    }
    if (findings.isError || !findings.data) {
        return (
            <p className="rounded border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive" role="alert">
                {httpStatusOf(findings.error) === 404 ? t('hierarchyAdvisor.errors.unknownRoot') : handleApiError(findings.error)}
            </p>
        )
    }

    const data = findings.data
    const turnOff = (ruleId: string) => {
        const rule = rules.data?.find((r) => r.ruleId === ruleId)
        // An override equal to the default is no override: send null, as the Rules view does.
        const severity = rule?.severity && rule.severity !== rule.defaultSeverity ? rule.severity : null
        setRule.mutate({ ruleId, enabled: false, severity })
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground">{data.root.name || data.root.id}</div>
                    <div className="text-xs text-muted-foreground">
                        {t('hierarchyAdvisor.objectsReviewed', { n: data.objectCount })}
                    </div>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => findings.refetch()}
                    title={t('hierarchyAdvisor.refresh')}
                    aria-label={t('hierarchyAdvisor.refresh')}
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${findings.isFetching ? 'animate-spin' : ''}`} />
                </Button>
            </div>

            {Object.keys(data.byObject ?? {}).length > 0 && <BadgeLegend />}
            <HiddenLine hidden={data.hidden} canConfigure={canConfigure} onShowView={onShowView} />
            {setRule.isError && (
                <p className="text-xs text-destructive" role="alert">{handleApiError(setRule.error)}</p>
            )}

            {LEVELS.map((level, i) => (
                <LevelSection key={level} level={level} index={i + 1} count={levelCount(data, level)}>
                    {level === 'missingObjects' ? (
                        <AdvisorSection
                            advisor={advisor}
                            suggestions={data.levels.missingObjects}
                            waiting={data.acceptedNotApplied ?? []}
                            onSelectObject={onSelectObject}
                            onShowObject={onShowObject}
                            onApplied={onApplied}
                        />
                    ) : (
                        <RuleGroups
                            level={level}
                            data={data}
                            canConfigure={canConfigure}
                            onSelectObject={onSelectObject}
                            onTurnOff={turnOff}
                            turningOff={setRule.isPending ? setRule.variables?.ruleId : undefined}
                        />
                    )}
                </LevelSection>
            ))}
        </div>
    )
}

function levelCount(data: Findings, level: LevelKey): number {
    if (level === 'missingObjects') return data.levels.missingObjects.length
    return (data.levels[level] as { count: number }[]).reduce((n, g) => n + (g.count || 0), 0)
}

/** How much the customer's own switches hide - always shown, so an empty panel never overclaims. */
function HiddenLine({
    hidden,
    canConfigure,
    onShowView,
}: {
    hidden: Findings['hidden']
    canConfigure: boolean
    onShowView: (v: View) => void
}) {
    const { t } = useTranslation()
    const rulesOff = hidden?.disabledRules ?? 0
    const excluded = hidden?.excluded ?? 0
    return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-dashed px-2 py-1.5 text-xs text-muted-foreground" data-testid="hierarchy-hidden">
            <EyeOff className="h-3.5 w-3.5 shrink-0" />
            {rulesOff + excluded === 0 ? (
                <span>{t('hierarchyAdvisor.hidden.none')}</span>
            ) : (
                <span>{t('hierarchyAdvisor.hidden.summary', { rules: rulesOff, excluded })}</span>
            )}
            {excluded > 0 && (
                <button type="button" className="font-medium text-primary hover:underline" onClick={() => onShowView('exclusions')}>
                    {t('hierarchyAdvisor.hidden.seeExclusions')}
                </button>
            )}
            {canConfigure && rulesOff > 0 && (
                <button type="button" className="font-medium text-primary hover:underline" onClick={() => onShowView('rules')}>
                    {t('hierarchyAdvisor.hidden.seeRules')}
                </button>
            )}
        </div>
    )
}

/**
 * What the numbered pills on the canvas mean (1 Oct 2026: a review drew them on the nodes and nothing
 * said what they were). The same pill, in each severity's colour, beside its name.
 */
function BadgeLegend() {
    const { t } = useTranslation()
    return (
        <div data-testid="badge-legend" className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-xs text-muted-foreground">
            <p>{t('hierarchyAdvisor.badgeLegend')}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                {(['high', 'medium', 'low'] as const).map((severity) => (
                    <span key={severity} className="inline-flex items-center gap-1.5">
                        <span
                            aria-hidden
                            className="inline-block h-4 min-w-4 rounded-full px-1 text-center text-[10px] font-bold leading-4 text-white"
                            style={{ background: SEVERITY_COLOUR[severity] }}
                        >
                            2
                        </span>
                        {t(`hierarchyAdvisor.severity.${severity}`)}
                    </span>
                ))}
            </div>
        </div>
    )
}

function LevelSection({ level, index, count, children }: { level: LevelKey; index: number; count: number; children: ReactNode }) {
    const { t } = useTranslation()
    return (
        <section data-level={level} className="flex flex-col gap-1.5" aria-labelledby={`hadv-level-${level}`}>
            <h3 id={`hadv-level-${level}`} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>{index}. {t(`hierarchyAdvisor.levels.${level}`)}</span>
                <span className="rounded-full bg-muted px-1.5 text-[10px] tabular-nums text-foreground" data-testid="level-count">
                    {count}
                </span>
            </h3>
            {/* What this section means, in a line: "missing groups" told nobody anything (Andrew, 1 Oct).
                Missing objects has the advisor's own introduction instead. */}
            {level !== 'missingObjects' && (
                <p className="text-[11px] leading-snug text-muted-foreground" data-testid="level-help">{t(`hierarchyAdvisor.levelHelp.${level}`)}</p>
            )}
            {children}
        </section>
    )
}

function SeverityDot({ severity }: { severity: Severity }) {
    return (
        <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ background: SEVERITY_COLOUR[severity] ?? '#9CA3AF' }}
        />
    )
}

function RuleGroups({
    level,
    data,
    canConfigure,
    onSelectObject,
    onTurnOff,
    turningOff,
}: {
    level: Exclude<LevelKey, 'missingObjects'>
    data: Findings
    canConfigure: boolean
    onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject']
    onTurnOff: (ruleId: string) => void
    turningOff?: string
}) {
    const { t } = useTranslation()
    const groups =
        level === 'missingGroups'
            ? orderFindings(data.levels.missingGroups).map((g) => ({
                  ruleId: g.ruleId,
                  label: ruleLabel(t, g.ruleId, g.capability?.name),
                  severity: g.severity,
                  count: g.count,
                  objects: g.services ?? [],
                  links: undefined as LinkRef[] | undefined,
              }))
            : orderFindings(data.levels[level]).map((g) => ({
                  ruleId: g.ruleId,
                  label: ruleLabel(t, g.ruleId),
                  severity: g.severity,
                  count: g.count,
                  objects: g.objects ?? [],
                  links: g.links,
              }))

    if (groups.length === 0) {
        return <p className="px-1 text-xs text-muted-foreground">{t('hierarchyAdvisor.levelEmpty')}</p>
    }
    return (
        <ul className="flex flex-col gap-1">
            {groups.map((g) => (
                <FindingGroup
                    key={g.ruleId}
                    {...g}
                    canConfigure={canConfigure}
                    onSelectObject={onSelectObject}
                    onTurnOff={() => onTurnOff(g.ruleId)}
                    turningOff={turningOff === g.ruleId}
                />
            ))}
        </ul>
    )
}

function FindingGroup({
    ruleId,
    label,
    severity,
    count,
    objects,
    links,
    canConfigure,
    onSelectObject,
    onTurnOff,
    turningOff,
}: {
    ruleId: string
    label: string
    severity: Severity
    count: number
    objects: ObjectRef[]
    links?: LinkRef[]
    canConfigure: boolean
    onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject']
    onTurnOff: () => void
    turningOff: boolean
}) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const listed = links ? links.length : objects.length
    return (
        <li data-rule={ruleId} className="rounded border bg-card">
            <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted/50"
            >
                {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                <SeverityDot severity={severity} />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground" data-testid="group-label">{label}</span>
                <span className="text-[10px] text-muted-foreground">{t(`hierarchyAdvisor.severity.${severity}`)}</span>
                <span className="rounded-full bg-muted px-1.5 text-[10px] font-semibold tabular-nums text-foreground" data-testid="group-count">
                    {count}
                </span>
            </button>
            {open && (
                <div className="flex flex-col gap-1 border-t px-2 py-1.5">
                    {canConfigure && (
                        <div className="flex justify-end">
                            <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={onTurnOff} disabled={turningOff}>
                                {t('hierarchyAdvisor.turnOffRule')}
                            </Button>
                        </div>
                    )}
                    <ul className="flex flex-col gap-1">
                        {links
                            ? links.map((l) => (
                                  <li key={l.mappingId} className="flex flex-wrap items-center gap-1 text-xs" data-testid="finding-link">
                                      <ObjectName obj={l.provider} onSelectObject={onSelectObject} />
                                      <span className="text-muted-foreground">→</span>
                                      <ObjectName obj={l.dependent} onSelectObject={onSelectObject} />
                                  </li>
                              ))
                            : objects.map((o) => (
                                  <FindingObject
                                      key={o.id}
                                      ruleId={ruleId}
                                      obj={o}
                                      canConfigure={canConfigure}
                                      onSelectObject={onSelectObject}
                                  />
                              ))}
                    </ul>
                    {count > listed && (
                        <p className="text-[11px] text-muted-foreground">{t('hierarchyAdvisor.moreNotListed', { n: count - listed })}</p>
                    )}
                </div>
            )}
        </li>
    )
}

function FindingObject({
    ruleId,
    obj,
    canConfigure,
    onSelectObject,
}: {
    ruleId: string
    obj: ObjectRef
    canConfigure: boolean
    onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject']
}) {
    const { t } = useTranslation()
    const [excluding, setExcluding] = useState(false)
    const exclude = useExclude()
    return (
        <li className="flex flex-col gap-1 text-xs" data-testid="finding-object" data-object-id={obj.id}>
            <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                    <ObjectName obj={obj} onSelectObject={onSelectObject} />
                    {obj.classification && <span className="ml-1 text-[11px] text-muted-foreground">· {obj.classification}</span>}
                </div>
                {canConfigure && !excluding && (
                    <Button variant="outline" size="sm" className="h-6 shrink-0 px-2 text-[11px]" onClick={() => setExcluding(true)}>
                        {t('hierarchyAdvisor.exclude.action')}
                    </Button>
                )}
            </div>
            {excluding && (
                <ReasonForm
                    label={t('hierarchyAdvisor.exclude.reasonLabel')}
                    submitLabel={t('hierarchyAdvisor.exclude.submit')}
                    pending={exclude.isPending}
                    error={
                        exclude.isError
                            ? httpStatusOf(exclude.error) === 409
                                ? t('hierarchyAdvisor.exclude.duplicate')
                                : handleApiError(exclude.error)
                            : null
                    }
                    onCancel={() => {
                        exclude.reset()
                        setExcluding(false)
                    }}
                    onSubmit={(reason) =>
                        exclude.mutate({ ruleId, objectId: obj.id, reason }, { onSuccess: () => setExcluding(false) })
                    }
                />
            )}
        </li>
    )
}

/**
 * An object named in a finding. On the canvas it selects and centres the node; elsewhere it is a
 * link to the object's own page. An object the canvas is not drawing (collapsed, filtered) says
 * so rather than doing nothing.
 */
function ObjectName({ obj, onSelectObject }: { obj: { id: string; name: string }; onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject'] }) {
    const { t } = useTranslation()
    const [missing, setMissing] = useState(false)
    const text = obj.name || obj.id
    if (!onSelectObject) {
        return (
            <Link to={`/objects/${encodeURIComponent(obj.id)}`} className="font-medium text-primary hover:underline" title={obj.id}>
                {text}
            </Link>
        )
    }
    return (
        <>
            <button
                type="button"
                className="font-medium text-primary hover:underline"
                title={obj.id}
                onClick={() => setMissing(onSelectObject(obj.id) === false)}
            >
                {text}
            </button>
            {missing && <span className="ml-1 text-[11px] text-muted-foreground">{t('hierarchyAdvisor.notOnCanvas')}</span>}
        </>
    )
}

/** A reason, required: excluding an object and dismissing a suggestion both need one (contract). */
function ReasonForm({
    label,
    submitLabel,
    pending,
    error,
    onSubmit,
    onCancel,
}: {
    label: string
    submitLabel: string
    pending: boolean
    error: string | null
    onSubmit: (reason: string) => void
    onCancel: () => void
}) {
    const { t } = useTranslation()
    const [reason, setReason] = useState('')
    const trimmed = reason.trim()
    return (
        <div className="flex flex-col gap-1.5 rounded border bg-muted/30 p-2">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-foreground">
                {label}
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className="text-xs" />
            </label>
            {error && <p className="text-[11px] text-destructive" role="alert">{error}</p>}
            <div className="flex justify-end gap-1.5">
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={onCancel} disabled={pending}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button size="sm" className="h-6 px-2 text-[11px]" disabled={!trimmed || pending} onClick={() => onSubmit(trimmed)}>
                    {submitLabel}
                </Button>
            </div>
        </div>
    )
}

// ── The advisor ──────────────────────────────────────────────────────────────────────────────

/** What was just done with a suggestion, said back once the findings have moved on without it. */
interface Done {
    result: ApplyResult
    anchor: string
}

function AdvisorSection({
    advisor: { review, running, ask, error },
    suggestions,
    waiting,
    onSelectObject,
    onShowObject,
    onApplied,
}: {
    advisor: AdvisorRun
    suggestions: Suggestion[]
    /** Accepted, and nothing done about it yet. */
    waiting: Suggestion[]
    onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject']
    onShowObject?: HierarchyAdvisorPanelProps['onShowObject']
    onApplied?: HierarchyAdvisorPanelProps['onApplied']
}) {
    const { t } = useTranslation()
    const [done, setDone] = useState<Done | null>(null)
    const applied = (result: ApplyResult, s: Suggestion) => {
        setDone({ result, anchor: s.anchor.name || s.anchor.id })
        onApplied?.(result)
    }

    return (
        <div className="flex flex-col gap-2" data-testid="hierarchy-advisor">
            <div className="flex flex-col gap-1.5 rounded border bg-card p-2 text-xs">
                <p className="text-muted-foreground">{t('hierarchyAdvisor.advisor.intro')}</p>
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" className="h-7 gap-1.5 text-xs" onClick={ask} disabled={running}>
                        <Sparkles className="h-3.5 w-3.5" />
                        {t('hierarchyAdvisor.advisor.ask')}
                    </Button>
                    {/* Decorative here: the status beside it says the same, and the header's spinner is the announced one. */}
                    {running && (
                        <span aria-hidden className="inline-flex shrink-0" data-testid="advisor-spinner">
                            <DotSphereSpinner size={48} />
                        </span>
                    )}
                    <span className="text-[11px] text-muted-foreground" data-testid="advisor-status" role="status">
                        {running ? (
                            t('hierarchyAdvisor.advisor.running')
                        ) : review === undefined ? null : review?.status === 'failed' ? (
                            <span className="text-destructive">
                                {t('hierarchyAdvisor.advisor.failed')}
                                {review.error ? `: ${review.error}` : ''}
                            </span>
                        ) : review?.status === 'done' ? (
                            <>
                                {t('hierarchyAdvisor.advisor.lastReview')}{' '}
                                {review.finishedAt && <DateTime date={review.finishedAt} format="datetime" />}
                                {' · '}
                                {t('hierarchyAdvisor.advisor.suggestionCount', { n: review.suggestionCount ?? 0 })}
                            </>
                        ) : (
                            t('hierarchyAdvisor.advisor.never')
                        )}
                    </span>
                </div>
                {error != null && <p className="text-[11px] text-destructive" role="alert">{handleApiError(error)}</p>}
            </div>

            {done && (
                <div className="flex items-start gap-2 rounded border border-green-600/40 bg-green-600/5 p-2 text-xs" data-testid="advisor-applied" role="status">
                    <div className="min-w-0 flex-1 font-medium text-foreground">
                        {t(`hierarchyAdvisor.advisor.${done.result.applied.created ? 'created' : 'linked'}.${done.result.position}`, {
                            object: done.result.applied.objectId,
                            anchor: done.anchor,
                        })}
                    </div>
                    <button type="button" onClick={() => setDone(null)} aria-label={t('common.close', 'Close')} className="text-muted-foreground hover:text-foreground">
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}

            {suggestions.length === 0 ? (
                <p className="px-1 text-xs text-muted-foreground">{t('hierarchyAdvisor.advisor.none')}</p>
            ) : (
                <ul className="flex flex-col gap-1.5">
                    {suggestions.map((s) => (
                        <SuggestionCard key={s.id} suggestion={s} onSelectObject={onSelectObject} onShowObject={onShowObject} onApplied={applied} />
                    ))}
                </ul>
            )}

            {waiting.length > 0 && (
                <div className="flex flex-col gap-1.5" data-testid="advisor-waiting">
                    <h4 className="px-1 text-[11px] font-semibold text-muted-foreground">{t('hierarchyAdvisor.advisor.waitingTitle')}</h4>
                    <ul className="flex flex-col gap-1.5">
                        {waiting.map((s) => (
                            <SuggestionCard key={s.id} suggestion={s} onSelectObject={onSelectObject} onShowObject={onShowObject} onApplied={applied} />
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

/**
 * One suggestion. Open: its evidence, then what can be done about it. Accepted and waiting: the
 * same actions without the argument for it - that was settled when it was accepted.
 */
function SuggestionCard({
    suggestion: s,
    onSelectObject,
    onShowObject,
    onApplied,
}: {
    suggestion: Suggestion
    onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject']
    onShowObject?: HierarchyAdvisorPanelProps['onShowObject']
    onApplied: (result: ApplyResult, s: Suggestion) => void
}) {
    const { t } = useTranslation()
    // Show: the canvas zooms in on the object the suggestion is about. It answers false when the
    // object is not drawn, and the card says so rather than doing nothing.
    const [notDrawn, setNotDrawn] = useState(false)
    const show = () => setNotDrawn(onShowObject?.(s.anchor.id) === false)
    const decide = useDecide()
    const apply = useApplyAdvice((result) => onApplied(result, s))
    // Linking changes the hierarchy (objects.update, as on the canvas); a new object is objects.create too.
    const canLink = useCan('objects.update')
    const canCreate = useCan('objects.create') && canLink
    const [dismissing, setDismissing] = useState(false)
    const [creating, setCreating] = useState(false)

    const open = s.status === 'open'
    const candidates = s.candidates ?? []
    const more = (s.candidateCount ?? candidates.length) - candidates.length
    // The rest of that classification: not what the advisor thinks is missing, so not listed until asked for.
    const others = s.otherCandidates ?? []
    const otherCount = s.otherCount ?? others.length
    const [showOthers, setShowOthers] = useState(false)
    const busy = decide.isPending || apply.isPending
    const run = (request: ApplyRequest) => apply.mutate({ adviceId: s.id, request })
    // Someone who cannot do the work can still say the suggestion is right - and so can someone
    // who could only link, when there is nothing to link.
    const canRecord = open && (!canLink || (candidates.length === 0 && !canCreate))

    return (
        <li className="flex flex-col gap-1.5 rounded border bg-card p-2 text-xs" data-testid="suggestion" data-advice-id={s.id}>
            <div className="font-medium text-foreground">{s.title}</div>
            <div className="flex flex-wrap items-center gap-x-1 text-[11px] text-muted-foreground" data-testid="suggestion-position" data-position={s.position}>
                <span>{t(s.position === 'above' ? 'hierarchyAdvisor.advisor.above' : 'hierarchyAdvisor.advisor.beneath')}</span>
                <ObjectName obj={s.anchor} onSelectObject={onSelectObject} />
            </div>
            <div className="text-[11px] text-foreground">
                {t('hierarchyAdvisor.advisor.expected', { classification: s.expected.classificationName, role: s.expected.role })}
            </div>
            {open && (
                <>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[11px]">
                        <dt className="font-medium text-muted-foreground">{t('hierarchyAdvisor.advisor.evidence')}</dt>
                        <dd className="text-foreground">{s.evidence}</dd>
                        <dt className="font-medium text-muted-foreground">{t('hierarchyAdvisor.advisor.reasoning')}</dt>
                        <dd className="text-foreground">{s.reasoning}</dd>
                    </dl>
                    {/* The advisor's confidence in itself, labelled as such: decision support, not a verdict. */}
                    <div className="text-[11px] text-muted-foreground" title={t('hierarchyAdvisor.advisor.confidenceHint')} data-testid="suggestion-confidence">
                        {t('hierarchyAdvisor.advisor.confidence', { level: t(`hierarchyAdvisor.severity.${s.confidence}`) })}
                    </div>
                </>
            )}

            {canLink && (
                // Part of this suggestion, and drawn as such: joined to the card by the accent on its left,
                // and headed with what linking one of these does and to which object (Andrew, 1 Oct -
                // a plain "Already in your estate" list did not look like it belonged to anything).
                <div className="flex flex-col gap-1.5 rounded-r border-l-2 border-primary bg-primary/5 py-1.5 pl-2.5 pr-2" data-testid="suggestion-candidates">
                    {candidates.length > 0 ? (
                        <>
                            <div className="flex items-start gap-1.5 text-[11px] font-medium text-foreground">
                                <CornerDownRight className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                                <span>
                                    {t(s.position === 'above' ? 'hierarchyAdvisor.advisor.inEstateAbove' : 'hierarchyAdvisor.advisor.inEstateBeneath', {
                                        classification: s.expected.classificationName,
                                        anchor: s.anchor.name,
                                    })}
                                </span>
                            </div>
                            <ul className="flex flex-col gap-1">
                                {candidates.map((c) => (
                                    <CandidateRow key={c.id} candidate={c} disabled={busy} onLink={() => run({ mode: 'link', objectId: c.id })} />
                                ))}
                            </ul>
                            {more > 0 && <p className="text-[11px] text-muted-foreground">{t('hierarchyAdvisor.advisor.moreCandidates', { n: more })}</p>}
                        </>
                    ) : (
                        <p className="text-[11px] text-muted-foreground" data-testid="suggestion-no-fit">
                            {otherCount > 0
                                ? t('hierarchyAdvisor.advisor.noFit')
                                : t('hierarchyAdvisor.advisor.noCandidates', { classification: s.expected.classificationName })}
                        </p>
                    )}
                    {/* Every other object of that classification was once listed here as if it were a
                        recommendation - every domain controller for a missing Entra ID tenant (Andrew,
                        2 Oct). They are a click away, for the object the advisor did not think of. */}
                    {otherCount > 0 && (
                        <>
                            <button
                                type="button"
                                className="flex items-center gap-1 self-start text-[11px] text-muted-foreground hover:text-foreground"
                                aria-expanded={showOthers}
                                data-testid="other-candidates-toggle"
                                onClick={() => setShowOthers((v) => !v)}
                            >
                                {showOthers ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                {t('hierarchyAdvisor.advisor.otherCandidates', { classification: s.expected.classificationName, n: otherCount })}
                            </button>
                            {showOthers && (
                                <>
                                    <ul className="flex flex-col gap-1" data-testid="other-candidates">
                                        {others.map((c) => (
                                            <CandidateRow key={c.id} candidate={c} disabled={busy} secondary onLink={() => run({ mode: 'link', objectId: c.id })} />
                                        ))}
                                    </ul>
                                    {otherCount > others.length && (
                                        <p className="text-[11px] text-muted-foreground">{t('hierarchyAdvisor.advisor.moreCandidates', { n: otherCount - others.length })}</p>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            {(decide.isError || apply.isError) && (
                <p className="text-[11px] text-destructive" role="alert">{handleApiError(apply.isError ? apply.error : decide.error)}</p>
            )}

            {creating ? (
                <CreateForm
                    suggestion={s}
                    pending={apply.isPending}
                    onCancel={() => {
                        apply.reset()
                        setCreating(false)
                    }}
                    onSubmit={(objectId, description) => run({ mode: 'create', objectId, description })}
                />
            ) : dismissing ? (
                <ReasonForm
                    label={t('hierarchyAdvisor.advisor.dismissReason')}
                    submitLabel={t('hierarchyAdvisor.advisor.dismiss')}
                    pending={decide.isPending}
                    error={null}
                    onCancel={() => setDismissing(false)}
                    onSubmit={(reason) => decide.mutate({ adviceId: s.id, decision: 'dismissed', reason })}
                />
            ) : (
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {/* On the left, away from the decisions: where on the canvas this is about. */}
                    {onShowObject && (
                        <div className="mr-auto flex items-center gap-1.5">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 gap-1 px-2 text-[11px]"
                                aria-label={`${t('hierarchyAdvisor.show')}: ${s.anchor.name}`}
                                onClick={show}
                            >
                                <LocateFixed className="h-3 w-3" />
                                {t('hierarchyAdvisor.show')}
                            </Button>
                            {notDrawn && <span className="text-[11px] text-muted-foreground">{t('hierarchyAdvisor.notOnCanvas')}</span>}
                        </div>
                    )}
                    {open && (
                        <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" disabled={busy} onClick={() => setDismissing(true)}>
                            {t('hierarchyAdvisor.advisor.dismiss')}
                        </Button>
                    )}
                    {canRecord && (
                        <Button
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            disabled={busy}
                            onClick={() => decide.mutate({ adviceId: s.id, decision: 'accepted' })}
                        >
                            {t('hierarchyAdvisor.advisor.recordAccepted')}
                        </Button>
                    )}
                    {/* Linking an existing object comes first; with none to link, creating one is the way. */}
                    {canCreate && (
                        <Button
                            variant={candidates.length > 0 ? 'outline' : 'default'}
                            size="sm"
                            className="h-6 px-2 text-[11px]"
                            disabled={busy}
                            data-primary={candidates.length === 0}
                            onClick={() => setCreating(true)}
                        >
                            {t('hierarchyAdvisor.advisor.createNew')}
                        </Button>
                    )}
                </div>
            )}
        </li>
    )
}

/** An object already in the estate that the suggestion could be: its name, id, site, and Link. */
function CandidateRow({ candidate: c, disabled, onLink, secondary = false }: {
    candidate: Candidate
    disabled: boolean
    onLink: () => void
    /** One of the "other" objects: its Link is outlined - the filled button is for what fits. */
    secondary?: boolean
}) {
    const { t } = useTranslation()
    return (
        <li className="flex items-center gap-2 text-[11px]" data-testid="candidate" data-object-id={c.id}>
            <div className="min-w-0 flex-1">
                <span className="font-medium text-foreground">{c.name || c.id}</span>
                {c.name && c.name !== c.id && <span className="ml-1 text-muted-foreground">{c.id}</span>}
                {c.location && <span className="ml-1 text-muted-foreground">· {c.location}</span>}
                {c.inHierarchy && (
                    <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] text-foreground" data-testid="candidate-in-hierarchy">
                        {t('hierarchyAdvisor.advisor.inHierarchy')}
                    </span>
                )}
            </div>
            <Button
                size="sm"
                variant={secondary ? 'outline' : 'default'}
                className="h-6 shrink-0 px-2 text-[11px]"
                disabled={disabled}
                onClick={onLink}
                aria-label={t('hierarchyAdvisor.advisor.linkObject', { object: c.name || c.id })}
            >
                {t('hierarchyAdvisor.advisor.link')}
            </Button>
        </li>
    )
}

/** A new object for the suggestion: its id and description - the same rules as the object form. */
function CreateForm({
    suggestion: s,
    pending,
    onSubmit,
    onCancel,
}: {
    suggestion: Suggestion
    pending: boolean
    onSubmit: (objectId: string, description: string) => void
    onCancel: () => void
}) {
    const { t } = useTranslation()
    const [objectId, setObjectId] = useState(() => suggestedObjectId(s))
    const [description, setDescription] = useState(s.expected.role ?? '')
    const id = objectId.trim()
    const idError = id && !OBJECT_ID_RE.test(id) ? t('visualization.addNode.objectIdInvalid', 'Object ID may only contain letters, numbers and hyphens.') : null
    return (
        <div className="flex flex-col gap-1.5 rounded border bg-muted/30 p-2" data-testid="suggestion-create">
            <label className="flex flex-col gap-1 text-[11px] font-medium text-foreground">
                {t('objects.objectID', 'Object ID')}
                <Input value={objectId} onChange={(e) => setObjectId(e.target.value)} maxLength={250} autoComplete="off" className="h-7 text-xs" />
            </label>
            {idError && <p className="text-[11px] text-destructive">{idError}</p>}
            <label className="flex flex-col gap-1 text-[11px] font-medium text-foreground">
                {t('objects.description', 'Description')}
                <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} className="h-7 text-xs" />
            </label>
            <div className="flex justify-end gap-1.5">
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={onCancel} disabled={pending}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button size="sm" className="h-6 px-2 text-[11px]" disabled={!id || !!idError || pending} onClick={() => onSubmit(id, description.trim())}>
                    {t('hierarchyAdvisor.advisor.createSubmit')}
                </Button>
            </div>
        </div>
    )
}

// ── Rules (hierarchy.configure) ──────────────────────────────────────────────────────────────

function RulesView() {
    const { t } = useTranslation()
    const rules = useHierarchyRules()
    const setRule = useSetRule()

    if (rules.isLoading) {
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" role="status" />
    }
    if (rules.isError) {
        return <p className="text-xs text-destructive" role="alert">{handleApiError(rules.error)}</p>
    }
    const save = (rule: Rule, next: { enabled?: boolean; severity?: Severity | null }) =>
        setRule.mutate({
            ruleId: rule.ruleId,
            enabled: next.enabled ?? rule.enabled,
            severity: next.severity !== undefined ? next.severity : rule.severity && rule.severity !== rule.defaultSeverity ? rule.severity : null,
        })

    return (
        <div className="flex flex-col gap-3" data-testid="hierarchy-rules">
            <p className="text-xs text-muted-foreground">{t('hierarchyAdvisor.rulesView.intro')}</p>
            {setRule.isError && <p className="text-xs text-destructive" role="alert">{handleApiError(setRule.error)}</p>}
            {LEVELS.filter((l) => l !== 'missingObjects').map((level) => {
                const inLevel = (rules.data ?? []).filter((r) => r.level === level)
                if (inLevel.length === 0) return null
                return (
                    <section key={level} className="flex flex-col gap-1">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`hierarchyAdvisor.levels.${level}`)}</h3>
                        <ul className="flex flex-col gap-1">
                            {inLevel.map((rule) => {
                                const label = ruleLabel(t, rule.ruleId, rule.name)
                                const override = rule.severity && rule.severity !== rule.defaultSeverity ? rule.severity : ''
                                return (
                                    <li key={rule.ruleId} data-rule={rule.ruleId} className="flex items-center gap-2 rounded border bg-card px-2 py-1.5 text-xs">
                                        <Switch
                                            checked={rule.enabled}
                                            onCheckedChange={(on) => save(rule, { enabled: on })}
                                            aria-label={label}
                                            className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span[data-state=checked]]:translate-x-3"
                                        />
                                        <span className={`min-w-0 flex-1 truncate ${rule.enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{label}</span>
                                        <select
                                            aria-label={t('hierarchyAdvisor.rulesView.severityFor', { rule: label })}
                                            value={override}
                                            onChange={(e) => save(rule, { severity: (e.target.value || null) as Severity | null })}
                                            className="h-6 rounded border bg-background px-1 text-[11px]"
                                        >
                                            <option value="">
                                                {t('hierarchyAdvisor.rulesView.defaultSeverity', { severity: t(`hierarchyAdvisor.severity.${rule.defaultSeverity}`) })}
                                            </option>
                                            {SEVERITIES.map((s) => (
                                                <option key={s} value={s}>
                                                    {t(`hierarchyAdvisor.severity.${s}`)}
                                                </option>
                                            ))}
                                        </select>
                                    </li>
                                )
                            })}
                        </ul>
                    </section>
                )
            })}
        </div>
    )
}

// ── Exclusions ───────────────────────────────────────────────────────────────────────────────

function ExclusionsView({
    root,
    canConfigure,
    onSelectObject,
}: {
    root: string
    canConfigure: boolean
    onSelectObject?: HierarchyAdvisorPanelProps['onSelectObject']
}) {
    const { t } = useTranslation()
    const exclusions = useHierarchyExclusions(root)
    const remove = useRemoveExclusion()
    // Only for a capability's name ("No DNS", not "No dns"); GET /rules is a hierarchy.review read.
    const rules = useHierarchyRules()
    const capabilityName = (ruleId: string) => rules.data?.find((r) => r.ruleId === ruleId)?.name

    if (exclusions.isLoading) {
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" role="status" />
    }
    if (exclusions.isError) {
        return <p className="text-xs text-destructive" role="alert">{handleApiError(exclusions.error)}</p>
    }
    const rows = exclusions.data ?? []
    return (
        <div className="flex flex-col gap-2" data-testid="hierarchy-exclusions">
            <p className="text-xs text-muted-foreground">{t('hierarchyAdvisor.exclusionsView.intro')}</p>
            {remove.isError && <p className="text-xs text-destructive" role="alert">{handleApiError(remove.error)}</p>}
            {rows.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('hierarchyAdvisor.exclusionsView.none')}</p>
            ) : (
                <ul className="flex flex-col gap-1">
                    {rows.map((x) => (
                        <li key={x.id} data-exclusion-id={x.id} className="flex flex-col gap-0.5 rounded border bg-card px-2 py-1.5 text-xs">
                            <div className="flex items-center gap-2">
                                <div className="min-w-0 flex-1">
                                    <ObjectName obj={x.object} onSelectObject={onSelectObject} />
                                    <span className="ml-1 text-[11px] text-muted-foreground">· {ruleLabel(t, x.ruleId, capabilityName(x.ruleId))}</span>
                                </div>
                                {canConfigure && (
                                    <button
                                        type="button"
                                        className="shrink-0 text-[11px] text-muted-foreground hover:text-destructive hover:underline"
                                        disabled={remove.isPending}
                                        onClick={() => remove.mutate(x.id)}
                                    >
                                        {t('hierarchyAdvisor.exclusionsView.remove')}
                                    </button>
                                )}
                            </div>
                            <div className="text-[11px] text-foreground">“{x.reason}”</div>
                            <div className="text-[10px] text-muted-foreground">
                                {x.createdBy != null && <>{t('hierarchyAdvisor.exclusionsView.by', { who: String(x.createdBy) })} · </>}
                                {x.createdAt && <DateTime date={x.createdAt} format="datetime" />}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
